import * as serializers from './serializers'
import {
  Apnef,
  ContractsAmountDataSerialized,
  CreditInsuranceDataSerialized,
  Fpd10DataSerialized,
  GetChartDataParams,
  MarketShareDataSerialized,
  OpenedAccountQuantityDataSerialized,
  Over30M3DataSerialized,
  OverDataSerialized,
  PotentialDataSerialized,
  ProductionDataSerialized,
  ProposalFunnelSerialized,
  SantanderAutoInsuranceDataSerialized,
  SantanderAutoTypeDataSerialized,
  SpreadPercentageDataSerialized,
  WebmotorsDataSerialized,
  TPropostasPagasItem,
  TPropostasAprovadasNaoEfetivadasItem,
  TProposals
} from './types'
import client from '../../client'
import { ResponseSuccess } from '../../types'

type ProposalsQueryParams = {
  searchParams?: string
  page?: number
  perPage?: number
  search?: string
}

type ProposalsListResponse<T> = T[] | { items?: T[]; ref_date?: string; refDate?: string }

type ProposalsMetricsItem = {
  ref_date: string
  title: string
  type: 'currency' | 'number'
  value: number
}

type ProposalsMetricsResponse = ProposalsMetricsItem[]

const buildUrlWithParams = (base: string, searchParams?: string) =>
  searchParams && searchParams.trim().length > 0 ? `${base}?${searchParams}` : base

const buildQueryParams = (searchParams?: string) => {
  if (!searchParams) return {}

  const parsedParams = new URLSearchParams(searchParams)

  const params: Record<string, string> = {}

  parsedParams.forEach((value, key) => {
    params[key] = value
  })

  return params
}

const mockProposalsOrder: TProposals = [
  { type: 'paid-numbers' },
  { type: 'paid' },
  { type: 'not-paid-numbers' },
  { type: 'not-paid' }
]

const mapPropostasPagasItem = (item: any): TPropostasPagasItem => ({
  date: item.date ?? '',
  customerName: item.customerName ?? item['customer-name'] ?? item['customer_name'] ?? '',
  customerDocument:
    item.customerDocument ?? item['customer-document'] ?? item['customer_document'] ?? '',
  storeName: item.storeName ?? item['store-name'] ?? item['store_name'] ?? '',
  storeTabNumber: item.storeTabNumber ?? item['store-tab-number'] ?? item['store_tab_number'] ?? '',
  modelName: item.modelName ?? item['model-name'] ?? item['model_name'] ?? '',
  mainValue: item.mainValue ?? item['main-value'] ?? item['main_value'] ?? 0,
  entryValue: item.entryValue ?? item['entry-value'] ?? item['entry_value'] ?? 0,
  durationMonths: item.durationMonths ?? item['duration-months'] ?? item['duration_months'] ?? '',
  storeBranch: item.storeBranch ?? item['store-branch'] ?? item['store_branch'] ?? '',
  subsegment: item.subsegment ?? item['subsegment'] ?? '',
  rating: item.rating ?? item['rating'] ?? '',
  microRegion: item.microRegion ?? item['micro-region'] ?? item['micro_region'] ?? '',
  tac: item.tac ?? item['tac'] ?? 0,
  accountHolderTag:
    item.accountHolderTag ?? item['account-holder-tag'] ?? item['account_holder_tag'] ?? '',
  hasLenderInsurance:
    item.hasLenderInsurance ??
    item['has-lender-insurance'] ??
    item['has_lender_insurance'] ??
    false,
  hasInsuranceHandWheel:
    item.hasInsuranceHandWheel ??
    item['has-insurance-hand-wheel'] ??
    item['has_insurance_hand_wheel'] ??
    false,
  hasSA: item.hasSA ?? item['has-sa'] ?? item['has_sa'] ?? false
})

const mapPropostasAprovadasNaoEfetivadasItem = (
  item: any
): TPropostasAprovadasNaoEfetivadasItem => ({
  numberProposal: item.numberProposal ?? item['number-proposal'] ?? item['number_proposal'] ?? '',
  date: item.date ?? '',
  hourProposal: item.hourProposal ?? item['hour-proposal'] ?? item['hour_proposal'] ?? '',
  storeTabNumber: item.storeTabNumber ?? item['store-tab-number'] ?? item['store_tab_number'] ?? '',
  storeName: item.storeName ?? item['store-name'] ?? item['store_name'] ?? '',
  modelName: item.modelName ?? item['model-name'] ?? item['model_name'] ?? '',
  storeBranch: item.storeBranch ?? item['store-branch'] ?? item['store_branch'] ?? '',
  microRegion: item.microRegion ?? item['micro-region'] ?? item['micro_region'] ?? '',
  subsegment: item.subsegment ?? item['subsegment'] ?? '',
  storeCnpj: item.storeCnpj ?? item['store-cnpj'] ?? item['store_cnpj'] ?? '',
  customerName: item.customerName ?? item['customer-name'] ?? item['customer_name'] ?? '',
  rating: item.rating ?? item['rating'] ?? '',
  hasAccountHolder:
    item.hasAccountHolder ?? item['has-account-holder'] ?? item['has_account_holder'] ?? false,
  mainValue: item.mainValue ?? item['main-value'] ?? item['main_value'] ?? 0,
  entryValue: item.entryValue ?? item['entry-value'] ?? item['entry_value'] ?? 0,
  inputRange: item.inputRange ?? item['input-range'] ?? item['input_range'] ?? '',
  installmentNumber:
    item.installmentNumber ?? item['installment-number'] ?? item['installment_number'] ?? '',
  hasSA: item.hasSA ?? item['has-sa'] ?? item['has_sa'] ?? false,
  hasLenderInsurance:
    item.hasLenderInsurance ??
    item['has-lender-insurance'] ??
    item['has_lender_insurance'] ??
    false,
  hasInsuranceHandWheel:
    item.hasInsuranceHandWheel ??
    item['has-insurance-hand-wheel'] ??
    item['has_insurance_hand_wheel'] ??
    false,
  userName: item.userName ?? item['user-name'] ?? item['user_name'] ?? '',
  userStdCode: item.userStdCode ?? item['user-std-code'] ?? item['user_std_code'],
  participation: item.participation ?? item['participation'],
  participationValue:
    item.participationValue ?? item['participation-value'] ?? item['participation_value']
})

export const getProposalFunnel = async (tab_number: string) =>
  serializers.getProposalFunnel(
    await client.get<ResponseSuccess<ProposalFunnelSerialized>>(
      `v1/stores/${tab_number}/panels/proposal-funnel`
    )
  )

export const getApnef = async (tab_number: string) =>
  await client.get<ResponseSuccess<Apnef>>(`v1/stores/${tab_number}/panels/apnef`)

export const getProduction = async (params: GetChartDataParams) =>
  serializers.getProduction(
    await client.get<ResponseSuccess<ProductionDataSerialized>>(
      `v1/stores/${params.tab_number}/panels/production`,
      {
        params: {
          year_month_qty: params.year_month_qty
        }
      }
    )
  )

export const getPotential = async (params: GetChartDataParams) =>
  serializers.getPotential(
    await client.get<ResponseSuccess<PotentialDataSerialized>>(
      `v1/stores/${params.tab_number}/panels/potential`,
      {
        params: {
          year_month_qty: params.year_month_qty
        }
      }
    )
  )

export const getMarketShare = async (params: GetChartDataParams) =>
  serializers.getMarketShare(
    await client.get<ResponseSuccess<MarketShareDataSerialized>>(
      `v1/stores/${params.tab_number}/panels/market-share`,
      {
        params: {
          year_month_qty: params.year_month_qty
        }
      }
    )
  )

export const getSantanderAutoInsurance = async (params: GetChartDataParams) =>
  serializers.getSantanderAutoInsurance(
    await client.get<ResponseSuccess<SantanderAutoInsuranceDataSerialized>>(
      `v1/stores/${params.tab_number}/panels/santander-auto-insurance`,
      {
        params: {
          year_month_qty: params.year_month_qty
        }
      }
    )
  )

export const getOpenedAccountQuantity = async (params: GetChartDataParams) =>
  serializers.getOpenedAccountQuantity(
    await client.get<ResponseSuccess<OpenedAccountQuantityDataSerialized>>(
      `v1/stores/${params.tab_number}/panels/opened-accounts-quantity`,
      {
        params: {
          year_month_qty: params.year_month_qty
        }
      }
    )
  )

export const getWebmotors = async (params: GetChartDataParams) =>
  serializers.getWebmotors(
    await client.get<ResponseSuccess<WebmotorsDataSerialized>>(
      `v1/stores/${params.tab_number}/panels/webmotors`,
      {
        params: {
          year_month_qty: params.year_month_qty
        }
      }
    )
  )

export const getFpd10 = async (params: GetChartDataParams) =>
  serializers.getFpd10(
    await client.get<ResponseSuccess<Fpd10DataSerialized>>(
      `v1/stores/${params.tab_number}/panels/fpd10`,
      {
        params: {
          year_month_qty: params.year_month_qty
        }
      }
    )
  )

export const getOver30M3 = async (params: GetChartDataParams) =>
  serializers.getOver30M3(
    await client.get<ResponseSuccess<Over30M3DataSerialized>>(
      `v1/stores/${params.tab_number}/panels/over30-m3`,
      {
        params: {
          year_month_qty: params.year_month_qty
        }
      }
    )
  )

export const getOver = async (params: GetChartDataParams) =>
  serializers.getOver(
    await client.get<ResponseSuccess<OverDataSerialized>>(
      `v1/stores/${params.tab_number}/panels/over`
    )
  )

export const getContractsAmount = async (params: GetChartDataParams) =>
  serializers.getContractsAmount(
    await client.get<ResponseSuccess<ContractsAmountDataSerialized>>(
      `v1/stores/${params.tab_number}/panels/contracts-quantity`,
      {
        params: {
          year_month_qty: params.year_month_qty
        }
      }
    )
  )

export const getSpreadPercentage = async (params: GetChartDataParams) =>
  serializers.getSpreadPercentage(
    await client.get<ResponseSuccess<SpreadPercentageDataSerialized>>(
      `v1/stores/${params.tab_number}/panels/spread`,
      {
        params: {
          year_month_qty: params.year_month_qty
        }
      }
    )
  )

export const getCreditInsurance = async (params: GetChartDataParams) =>
  serializers.getCreditInsurance(
    await client.get<ResponseSuccess<CreditInsuranceDataSerialized>>(
      `v1/stores/${params.tab_number}/panels/credit-insurance`,
      {
        params: {
          year_month_qty: params.year_month_qty
        }
      }
    )
  )

export const getSantanderAutoType = async (params: GetChartDataParams) =>
  serializers.getSantanderAutoType(
    await client.get<ResponseSuccess<SantanderAutoTypeDataSerialized>>(
      `v1/stores/${params.tab_number}/panels/santander-auto-type`,
      {
        params: {
          year_month_qty: params.year_month_qty
        }
      }
    )
  )

export const getProposalsPage = async ({ searchParams }: ProposalsQueryParams) => {
  const url = buildUrlWithParams(`v1/vehicle-proposal`, searchParams)

  try {
    const response = await client.get<ResponseSuccess<TProposals>>(url)

    const data = response.data.data ?? []

    if (data.length > 0) {
      return data
    }
  } catch (error) {
    // noop, will fall back below
  }

  return mockProposalsOrder
}

export const getPropostasPagas = async ({
  searchParams,
  page,
  perPage,
  search
}: ProposalsQueryParams) => {
  const response = await client.get<ResponseSuccess<ProposalsListResponse<TPropostasPagasItem>>>(
    `v1/vehicle-proposal/paid`,
    {
      params: {
        ...buildQueryParams(searchParams),
        page: page ?? 1,
        per_page: perPage ?? 10,
        search: search?.trim() ?? undefined
      }
    }
  )

  const payload = response.data.data

  const itemsArray = Array.isArray(payload) ? payload : payload?.items ?? []

  const mappedItems = itemsArray.map(mapPropostasPagasItem)

  const refDate = Array.isArray(payload) ? undefined : payload?.ref_date ?? payload?.refDate

  const pagination = response.data.metadata?.pagination

  return {
    data: mappedItems,
    refDate,
    total: pagination?.total ?? mappedItems.length,
    page: pagination?.current_page ?? page ?? 1,
    perPage: pagination?.per_page ?? perPage ?? 10
  }
}

export const getPropostasPagasMetrics = async ({ searchParams }: ProposalsQueryParams) => {
  const url = buildUrlWithParams(`v1/vehicle-proposal/paid-numbers`, searchParams)

  const response = await client.get<ResponseSuccess<ProposalsMetricsResponse>>(url)

  const payload = response.data.data

  const metrics = payload ?? []

  const refDate = metrics.length ? metrics[0].ref_date : undefined

  return {
    metrics,
    refDate
  }
}

export const getPropostasAprovadasNaoEfetivadas = async ({
  searchParams,
  page,
  perPage,
  search
}: ProposalsQueryParams) => {
  const response = await client.get<
    ResponseSuccess<ProposalsListResponse<TPropostasAprovadasNaoEfetivadasItem>>
  >(`v1/vehicle-proposal/not-paid`, {
    params: {
      ...buildQueryParams(searchParams),
      page: page ?? 1,
      per_page: perPage ?? 10,
      search: search?.trim() ?? undefined
    }
  })

  const payload = response.data.data

  const itemsArray = Array.isArray(payload) ? payload : payload?.items ?? []

  const mappedItems = itemsArray.map(mapPropostasAprovadasNaoEfetivadasItem)

  const refDate = Array.isArray(payload) ? undefined : payload?.ref_date ?? payload?.refDate

  const pagination = response.data.metadata?.pagination

  return {
    data: mappedItems,
    refDate,
    total: pagination?.total ?? mappedItems.length,
    page: pagination?.current_page ?? page ?? 1,
    perPage: pagination?.per_page ?? perPage ?? 10
  }
}

export const getPropostasAprovadasNaoEfetivadasMetrics = async ({
  searchParams
}: ProposalsQueryParams) => {
  const url = buildUrlWithParams(`v1/vehicle-proposal/not-paid-numbers`, searchParams)

  const response = await client.get<ResponseSuccess<ProposalsMetricsResponse>>(url)

  const payload = response.data.data

  const metrics = payload ?? []

  const refDate = metrics.length ? metrics[0].ref_date : undefined

  return {
    metrics,
    refDate
  }
}
